import { Component, DestroyRef, computed, effect, inject, signal, untracked } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { DecimalPipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { BaseChartDirective } from 'ng2-charts';
import {
  ArcElement,
  BarElement,
  BarController,
  CategoryScale,
  Chart,
  DoughnutController,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { AnalyticsService } from '../../core/services/analytics.service';
import { DateRangeService } from '../../core/services/date-range.service';
import { OverviewResponse } from '../../core/models/overview.models';
import { LocalDatePipe } from '../../shared/pipes/local-date.pipe';
import { MetricCardComponent } from '../../shared/components/metric-card/metric-card.component';

Chart.register(
  DoughnutController,
  ArcElement,
  BarController,
  BarElement,
  Legend,
  Tooltip,
  CategoryScale,
  LinearScale,
);

@Component({
  selector: 'app-overview-page',
  standalone: true,
  imports: [DecimalPipe, RouterLink, BaseChartDirective, LocalDatePipe, MetricCardComponent],
  templateUrl: './overview-page.component.html',
  styleUrl: './overview-page.component.scss',
})
export class OverviewPageComponent {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly dateRange = inject(DateRangeService);
  private readonly destroyRef = inject(DestroyRef);
  private currentLoadId = 0;

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly data = signal<OverviewResponse | null>(null);
  readonly hasOverviewActivity = computed(() => {
    const overview = this.data();

    if (!overview) {
      return false;
    }

    const verdictTotal =
      (overview.verdictDistribution.approved ?? 0) +
      (overview.verdictDistribution.needsChanges ?? 0) +
      (overview.verdictDistribution.blocked ?? 0) +
      (overview.verdictDistribution.failed ?? 0);

    return (
      (overview.totals.pullRequestsReviewed ?? 0) > 0 ||
      (overview.totals.issuesFound ?? 0) > 0 ||
      (overview.totals.completedReviewRuns ?? 0) > 0 ||
      verdictTotal > 0 ||
      overview.categoryBreakdown.some((item) => item.count > 0) ||
      overview.topRepositories.some((item) => item.pullRequestsReviewed > 0 || item.issuesFound > 0)
    );
  });

  readonly verdictChartData = signal({
    labels: ['Approved', 'Needs Changes', 'Blocked', 'Failed'],
    datasets: [
      {
        data: [0, 0, 0, 0],
        backgroundColor: ['#16a34a', '#f59e0b', '#dc2626', '#475569'],
        borderWidth: 0,
      },
    ],
  });

  readonly categoryChartData = signal({
    labels: [] as string[],
    datasets: [
      {
        label: 'Issues',
        data: [] as number[],
        backgroundColor: ['#1d4ed8', '#f59e0b', '#16a34a', '#ef4444', '#8b5cf6'],
        borderRadius: 10,
      },
    ],
  });

  constructor() {
    effect(() => {
      this.dateRange.appliedRange();
      this.dateRange.appliedRevision();
      untracked(() => this.load());
    });
  }

  load(): void {
    const loadId = ++this.currentLoadId;
    this.loading.set(true);
    this.error.set(null);
    this.data.set(null);

    this.analyticsService
      .getOverview(this.dateRange.toQueryParams())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          if (loadId === this.currentLoadId) {
            this.loading.set(false);
          }
        }),
      )
      .subscribe({
        next: (response) => {
          if (loadId !== this.currentLoadId) {
            return;
          }

          try {
            if (!response?.totals || !response?.verdictDistribution || !Array.isArray(response?.categoryBreakdown)) {
              throw new Error('Overview response is missing expected fields.');
            }

            this.applyOverview(response);
          } catch (mappingError) {
            console.error('Overview response handling failed', mappingError, response);
            this.data.set(null);
            this.error.set('Overview response could not be rendered. Check the browser console for the payload.');
          }
        },
        error: (error: unknown) => {
          if (loadId !== this.currentLoadId) {
            return;
          }

          console.error('Overview request failed', error);
          this.error.set(this.formatError(error, 'Overview data is unavailable.'));
        },
      });
  }

  private applyOverview(response: OverviewResponse): void {
    const activeCategories = response.categoryBreakdown.filter((item) => item.count > 0);
    const activeRepositories = response.topRepositories.filter(
      (item) => item.pullRequestsReviewed > 0 || item.issuesFound > 0,
    );

    this.data.set(response);
    this.verdictChartData.set({
      ...this.verdictChartData(),
      datasets: [
        {
          ...this.verdictChartData().datasets[0],
          data: [
            response.verdictDistribution.approved ?? 0,
            response.verdictDistribution.needsChanges ?? 0,
            response.verdictDistribution.blocked ?? 0,
            response.verdictDistribution.failed ?? 0,
          ],
        },
      ],
    });
    this.categoryChartData.set({
      labels: activeCategories.map((item) => item.category),
      datasets: [
        {
          ...this.categoryChartData().datasets[0],
          data: activeCategories.map((item) => item.count),
        },
      ],
    });
    this.data.set({
      ...response,
      categoryBreakdown: activeCategories,
      topRepositories: activeRepositories,
    });
  }

  private formatError(error: unknown, fallback: string): string {
    if ((error as { name?: string })?.name === 'TimeoutError') {
      return 'Overview request timed out after 8s. The backend is not responding for the selected range.';
    }

    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) {
        return `Overview request could not reach the backend${error.url ? ` @ ${error.url}` : ''}. Check backend availability, CORS, and network access from the browser.`;
      }

      const details = typeof error.error === 'string' ? error.error : JSON.stringify(error.error);
      const requestUrl = error.url ? ` @ ${error.url}` : '';
      return `${fallback} (${error.status || 'network'}${requestUrl}: ${details || error.message})`;
    }

    return fallback;
  }
}
